import React, { FC } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, UseFormProps, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { withController } from "@utils";

import { Notification, Text } from "@ui/elements";
import { Input, Label } from "@ui/forms";
import { Stack } from "@ui/layout";

import { Filter } from "@components";

import { initialFilters } from "../../pages/app/ai-performance/AIPerformance";
import { ErrorType } from "../../services/api/custom-instance";
import { FilterTags } from "../MultiFilter/FilterTags/FilterTags";

export const useStrategyForm = (options?: UseFormProps) => {
  const { t } = useTranslation();
  const validationSchema = Yup.object({
    name: Yup.string().required(t("messages.strategy-name-required")),
    filters: Yup.array()
      .test("at-least-one-enabled", t("messages.min-one-filter"), (filters) =>
        (filters as Filter[]).some((filter) => filter.enabled),
      )
      .required(t("messages.min-one-filter-required")),
  });
  const form = useForm<StrategyFormValues>({
    defaultValues: {
      name: "",
      filters: initialFilters,
      ...(options?.defaultValues || {}),
    },
    resolver: yupResolver(validationSchema),
  });

  return form;
};

type StrategyFormValues = {
  id?: string;
  name: string;
  filters: Filter[];
};

const ControlledInput = withController<StrategyFormValues>()(Input);

type StrategyFormProps = {
  form: UseFormReturn<StrategyFormValues>;
  createError?: ErrorType<unknown> | null;
  updateError?: ErrorType<unknown> | null;
};

export const StrategyForm: FC<StrategyFormProps> = ({
  form,
  createError,
  updateError,
}) => {
  const { t } = useTranslation();
  const filters = form.watch("filters");

  return (
    <Stack direction="column">
      {createError && (
        <Notification color="danger">
          <Text weight="bold">{t("messages.failed-strategy-create")}</Text>
          <Text>{createError.response?.data.message}</Text>
        </Notification>
      )}

      {updateError && (
        <Notification color="danger">
          <Text weight="bold">{t("messages.failed-strategy-update")}</Text>
          <Text>{updateError.response?.data.message}</Text>
        </Notification>
      )}

      <ControlledInput
        label={t("labels.strategy-name")}
        name="name"
        rules={{
          required: t("messages.strategy-name-required"),
        }}
        control={form.control}
      />

      <Stack direction="column" gap="xxs">
        <Label>{t("labels.filters")}</Label>
        <Stack direction="column" gap="none">
          <FilterTags
            filters={filters}
            onChange={(filters) => form.setValue("filters", filters)}
          />
          {form.formState.errors.filters && (
            <p className="help is-danger">
              {form.formState.errors.filters.message}
            </p>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
